/**
 * Normalising the contract form's clearable ids before saving.
 *
 * react-final-form **removes a cleared field from `values` entirely** — a cleared field and
 * an untouched one are indistinguishable by the time the form submits. apiserver's
 * `Entity.update()` skips fields that are `undefined`, so a cleared id was never written and
 * the previous value survived (bugs#30).
 *
 * Listing the clearable ids here turns "absent" back into an explicit `null`, which is what
 * the API needs in order to clear the column.
 */

import { isBlank } from '@utriper/core';

/**
 * Optional contract ids a user is allowed to clear.
 *
 * `discountId` and `markupId` were previously handled by two ad-hoc `if (!values.x)` lines;
 * `hostId` and `experiencerId` never were, which was bugs#30. Keeping them in one list means
 * the next nullable id does not arrive with the same bug.
 */
const CLEARABLE_IDS = ['discountId', 'markupId', 'hostId', 'experiencerId'];

/**
 * An id, or null when it is not set.
 *
 * Blank and zero both count as "not set" — ids start at 1, and the lines this replaces
 * treated `0` as unset, so that has to keep working. A present value is returned untouched,
 * so a number stays a number rather than becoming a string.
 *
 * @param {unknown} value
 * @returns {unknown|null}
 */
const idOrNull = (value) => (isBlank(value) || Number(value) === 0 ? null : value);

/**
 * A copy of the submitted values with every clearable id present and explicit.
 *
 * @param {object} values Form values, as react-final-form hands them over.
 * @returns {object} A new object; the input is not mutated.
 */
export const normalizeClearableIds = (values = {}) => {
    const out = { ...values };
    CLEARABLE_IDS.forEach((key) => {
        out[key] = idOrNull(out[key]);
    });
    return out;
};
